import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import {
  FlaskConical, BarChart3, Sparkles, Calendar, Plus, Search, Filter,
  ChevronRight, ChevronDown, ChevronUp, Clock, Users, Target, Zap,
  Heart, Brain, Eye, Edit, Copy, Archive, RotateCcw, GripVertical,
  CheckCircle, AlertTriangle, Loader2, X, ArrowLeft, Play, Save,
  Layers, BookOpen, ListOrdered, Tag, Star, Activity, GitBranch,
  ArrowUpRight, MessageCircle, Shield, FileWarning, TrendingUp,
  TrendingDown, Minus, Gauge
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_CONFIG = {
  rompehielos: { label: 'Rompehielos', color: '#22c55e', icon: Zap },
  atraccion_inicial: { label: 'Atracción Inicial', color: '#f59e0b', icon: Heart },
  compatibilidad_emocional: { label: 'Compatibilidad Emocional', color: '#ec4899', icon: Brain },
  valores: { label: 'Valores', color: '#8b5cf6', icon: Target },
  quimica: { label: 'Química', color: '#ef4444', icon: Sparkles },
  ludica: { label: 'Lúdica', color: '#06b6d4', icon: Play },
  seleccion_privada: { label: 'Selección Privada', color: '#6366f1', icon: Eye },
  cierre_emocional: { label: 'Cierre Emocional', color: '#f97316', icon: Star },
  conversacion_guiada: { label: 'Conversación Guiada', color: '#14b8a6', icon: BookOpen },
  actividad_sensorial: { label: 'Actividad Sensorial', color: '#d946ef', icon: Activity },
  vulnerabilidad: { label: 'Vulnerabilidad', color: '#be185d', icon: Heart },
  continuidad: { label: 'Continuidad', color: '#0ea5e9', icon: RotateCcw }
};

const STAGE_LABELS = {
  apertura: 'Apertura', calentamiento: 'Calentamiento', interaccion: 'Interacción',
  profundidad: 'Profundidad', seleccion: 'Selección', cierre: 'Cierre', transicion: 'Transición'
};

const LevelDots = ({ level, max = 5, color = '#8B1A1A' }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: max }, (_, i) => (
      <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: i < level ? color : '#333' }} />
    ))}
  </div>
);

const Badge = ({ children, color = '#8B1A1A', small }) => (
  <span className={`inline-flex items-center rounded-full border ${small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'}`}
    style={{ borderColor: color + '60', color, backgroundColor: color + '15' }}>
    {children}
  </span>
);

// ============================= OVERVIEW TAB =============================
const OverviewTab = ({ overview, onNavigate }) => {
  if (!overview) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#8B1A1A]" size={32} /></div>;

  const cards = [
    { label: 'Dinámicas Activas', value: overview.dynamics?.active || 0, icon: Sparkles, color: '#22c55e' },
    { label: 'Plantillas de Flujo', value: overview.flows?.templates || 0, icon: Layers, color: '#8b5cf6' },
    { label: 'Eventos Activos', value: overview.events?.active || 0, icon: Calendar, color: '#f59e0b' },
    { label: 'Eventos Completados', value: overview.events?.completed || 0, icon: CheckCircle, color: '#06b6d4' },
    { label: 'Matches Totales', value: overview.connections?.total_matches || 0, icon: Heart, color: '#ec4899' },
    { label: 'Tasa Conversión', value: `${overview.connections?.conversion_rate || 0}%`, icon: TrendingUp, color: '#8B1A1A' }
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {cards.map((c, i) => (
          <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <c.icon size={16} style={{ color: c.color }} />
              <span className="text-xs text-gray-500 uppercase tracking-wider">{c.label}</span>
            </div>
            <div className="text-2xl font-bold text-white">{c.value}</div>
          </div>
        ))}
      </div>

      {overview.dynamics?.by_category && Object.keys(overview.dynamics.by_category).length > 0 && (
        <div className="bg-[#111] border border-[#222] rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Dinámicas por Categoría</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(overview.dynamics.by_category).map(([cat, count]) => {
              const cfg = CATEGORY_CONFIG[cat] || { label: cat, color: '#666' };
              return <Badge key={cat} color={cfg.color}>{cfg.label}: {count}</Badge>;
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <button onClick={() => onNavigate('dynamics')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-dynamics">
          <Sparkles size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Biblioteca de Dinámicas</div>
          <div className="text-xs text-gray-500 mt-1">Crear, editar y organizar dinámicas</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('flows')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-flows">
          <ListOrdered size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Constructor de Flujo</div>
          <div className="text-xs text-gray-500 mt-1">Armar secuencia de dinámicas por evento</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('events')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-events">
          <Calendar size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Eventos Piloto</div>
          <div className="text-xs text-gray-500 mt-1">Gestionar y enriquecer eventos</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('journeys')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-journeys">
          <GitBranch size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Relational Journeys</div>
          <div className="text-xs text-gray-500 mt-1">Progresión relacional entre pares de usuarios</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('actions')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-actions">
          <Zap size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Acciones del Sistema</div>
          <div className="text-xs text-gray-500 mt-1">Decisiones operativas: invitar, priorizar, facilitar</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('incidents')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-incidents">
          <FileWarning size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Panel de Incidencias</div>
          <div className="text-xs text-gray-500 mt-1">Reportar y rastrear problemas operativos</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
        <button onClick={() => onNavigate('metrics')} className="bg-[#111] border border-[#222] rounded-lg p-4 text-left hover:border-[#8B1A1A] transition-colors group" data-testid="nav-metrics">
          <Activity size={20} className="text-[#8B1A1A] mb-2" />
          <div className="text-white font-medium">Métricas y Learning</div>
          <div className="text-xs text-gray-500 mt-1">Métricas por evento, dinámica y sistema</div>
          <ChevronRight size={16} className="text-gray-600 mt-2 group-hover:text-[#8B1A1A] transition-colors" />
        </button>
      </div>
    </div>
  );
};

// ============================= DYNAMICS TAB =============================
const TrendingUpAlias = Activity; // alias for dynamics tab

const DynamicsTab = ({ onBack }) => {
  const [dynamics, setDynamics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedDynamic, setSelectedDynamic] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchDynamics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (catFilter) params.set('category', catFilter);
      if (stageFilter) params.set('stage', stageFilter);
      params.set('status', showArchived ? 'archived' : 'active');
      const { data } = await axios.get(`${API}/lab/dynamics?${params}`, { withCredentials: true });
      setDynamics(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [catFilter, stageFilter, showArchived]);

  useEffect(() => { fetchDynamics(); }, [fetchDynamics]);

  const handleAction = async (action, dynamicId) => {
    try {
      if (action === 'duplicate') await axios.post(`${API}/lab/dynamics/${dynamicId}/duplicate`, {}, { withCredentials: true });
      else if (action === 'archive') await axios.post(`${API}/lab/dynamics/${dynamicId}/archive`, {}, { withCredentials: true });
      else if (action === 'activate') await axios.post(`${API}/lab/dynamics/${dynamicId}/activate`, {}, { withCredentials: true });
      fetchDynamics();
    } catch (e) { console.error(e); }
  };

  const filtered = dynamics.filter(d =>
    !search || d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.tags?.some(t => t.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 bg-[#8B1A1A] text-white px-3 py-1.5 rounded-lg text-sm hover:bg-[#a52020] transition-colors" data-testid="create-dynamic-btn">
          <Plus size={14} /> Nueva Dinámica
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar dinámicas..." className="w-full bg-[#111] border border-[#222] rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-600 focus:border-[#8B1A1A] outline-none" data-testid="dynamics-search" />
        </div>
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-[#111] border border-[#222] rounded-lg px-3 py-2 text-sm text-white outline-none" data-testid="category-filter">
          <option value="">Todas las categorías</option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} className="bg-[#111] border border-[#222] rounded-lg px-3 py-2 text-sm text-white outline-none">
          <option value="">Todas las etapas</option>
          {Object.entries(STAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <button onClick={() => setShowArchived(!showArchived)} className={`px-3 py-2 rounded-lg text-sm border ${showArchived ? 'border-[#8B1A1A] text-[#8B1A1A]' : 'border-[#222] text-gray-500'}`}>
          <Archive size={14} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8B1A1A]" size={24} /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {dynamics.length === 0 ? 'No hay dinámicas. Crea la primera o carga el starter pack.' : 'Sin resultados para esta búsqueda.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => {
            const cfg = CATEGORY_CONFIG[d.category] || { label: d.category, color: '#666', icon: Sparkles };
            const Icon = cfg.icon;
            const isExpanded = expandedId === d.dynamic_id;
            return (
              <div key={d.dynamic_id} className="bg-[#111] border border-[#222] rounded-lg overflow-hidden hover:border-[#333] transition-colors" data-testid={`dynamic-${d.dynamic_id}`}>
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : d.dynamic_id)}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + '20' }}>
                    <Icon size={16} style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{d.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge color={cfg.color} small>{cfg.label}</Badge>
                      <span className="text-[10px] text-gray-600">{d.duration_minutes}min</span>
                      {d.suggested_stage && <Badge small color="#555">{STAGE_LABELS[d.suggested_stage] || d.suggested_stage}</Badge>}
                    </div>
                  </div>
                  <div className="hidden md:flex items-center gap-4 text-[10px] text-gray-500">
                    <div className="text-center"><div className="mb-0.5">Profundidad</div><LevelDots level={d.depth_level} color={cfg.color} /></div>
                    <div className="text-center"><div className="mb-0.5">Interacción</div><LevelDots level={d.interaction_level} color="#f59e0b" /></div>
                    <div className="text-center"><div className="mb-0.5">Energía</div><LevelDots level={d.energy_level} color="#22c55e" /></div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={e => { e.stopPropagation(); handleAction('duplicate', d.dynamic_id); }} className="p-1.5 text-gray-600 hover:text-white" title="Duplicar"><Copy size={14} /></button>
                    <button onClick={e => { e.stopPropagation(); handleAction(showArchived ? 'activate' : 'archive', d.dynamic_id); }} className="p-1.5 text-gray-600 hover:text-white" title={showArchived ? 'Activar' : 'Archivar'}>
                      {showArchived ? <RotateCcw size={14} /> : <Archive size={14} />}
                    </button>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-[#222] p-4 space-y-3 text-sm">
                    <div><span className="text-gray-500">Objetivo:</span> <span className="text-gray-300">{d.objective}</span></div>
                    {d.description && <div><span className="text-gray-500">Descripción:</span> <span className="text-gray-300">{d.description}</span></div>}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {d.instructions_staff && <div className="bg-[#0a0a0a] rounded p-2"><span className="text-gray-500 text-xs block mb-1">Instrucciones Staff</span><span className="text-gray-400 text-xs">{d.instructions_staff}</span></div>}
                      {d.instructions_participants && <div className="bg-[#0a0a0a] rounded p-2"><span className="text-gray-500 text-xs block mb-1">Instrucciones Participantes</span><span className="text-gray-400 text-xs">{d.instructions_participants}</span></div>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span><Users size={12} className="inline mr-1" />{d.min_participants}-{d.max_participants} personas</span>
                      <span><Clock size={12} className="inline mr-1" />{d.duration_minutes} min</span>
                      {d.materials_required?.length > 0 && <span>Materiales: {d.materials_required.join(', ')}</span>}
                    </div>
                    {d.expected_outcome && <div className="text-xs"><span className="text-gray-500">Resultado esperado:</span> <span className="text-gray-400">{d.expected_outcome}</span></div>}
                    {d.risks_notes && <div className="text-xs"><span className="text-amber-600">Riesgos:</span> <span className="text-gray-400">{d.risks_notes}</span></div>}
                    {d.tags?.length > 0 && <div className="flex flex-wrap gap-1">{d.tags.map(t => <Badge key={t} small color="#444">{t}</Badge>)}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <DynamicFormModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); fetchDynamics(); }} />}
    </div>
  );
};

// ============================= DYNAMIC FORM MODAL =============================
const DynamicFormModal = ({ onClose, onSaved, initial }) => {
  const [form, setForm] = useState(initial || {
    name: '', category: 'rompehielos', objective: '', description: '',
    instructions_staff: '', instructions_participants: '',
    duration_minutes: 15, depth_level: 3, interaction_level: 3, energy_level: 3,
    min_participants: 4, max_participants: 20, suggested_stage: 'interaccion',
    expected_outcome: '', success_criteria: '', risks_notes: '', tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.objective) return;
    setSaving(true);
    try {
      if (initial?.dynamic_id) {
        await axios.put(`${API}/lab/dynamics/${initial.dynamic_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/lab/dynamics`, form, { withCredentials: true });
      }
      onSaved();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const F = (label, field, type = 'text', opts = {}) => (
    <div className={opts.full ? 'col-span-2' : ''}>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      {type === 'textarea' ? (
        <textarea value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })}
          className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#8B1A1A] resize-none" rows={3} />
      ) : type === 'select' ? (
        <select value={form[field] || ''} onChange={e => setForm({ ...form, [field]: e.target.value })}
          className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-sm text-white outline-none">
          {opts.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : type === 'range' ? (
        <div className="flex items-center gap-2">
          <input type="range" min={1} max={5} value={form[field] || 3} onChange={e => setForm({ ...form, [field]: parseInt(e.target.value) })} className="flex-1 accent-[#8B1A1A]" />
          <span className="text-xs text-white w-4 text-center">{form[field]}</span>
        </div>
      ) : (
        <input type={type} value={form[field] || ''} onChange={e => setForm({ ...form, [field]: type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })}
          className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#8B1A1A]" />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4 pt-10" data-testid="dynamic-form-modal">
      <div className="bg-[#0d0d0d] border border-[#222] rounded-xl w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#222]">
          <h3 className="text-white font-medium">{initial ? 'Editar Dinámica' : 'Nueva Dinámica'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
          {F('Nombre *', 'name', 'text', { full: true })}
          {F('Categoría', 'category', 'select', { options: Object.entries(CATEGORY_CONFIG).map(([k, v]) => ({ value: k, label: v.label })) })}
          {F('Etapa sugerida', 'suggested_stage', 'select', { options: Object.entries(STAGE_LABELS).map(([k, v]) => ({ value: k, label: v })) })}
          {F('Objetivo *', 'objective', 'textarea', { full: true })}
          {F('Descripción', 'description', 'textarea', { full: true })}
          {F('Instrucciones Staff', 'instructions_staff', 'textarea', { full: true })}
          {F('Instrucciones Participantes', 'instructions_participants', 'textarea', { full: true })}
          {F('Duración (min)', 'duration_minutes', 'number')}
          {F('Min participantes', 'min_participants', 'number')}
          {F('Max participantes', 'max_participants', 'number')}
          <div />
          {F('Profundidad', 'depth_level', 'range')}
          {F('Interacción', 'interaction_level', 'range')}
          {F('Energía', 'energy_level', 'range')}
          <div />
          {F('Resultado esperado', 'expected_outcome', 'textarea', { full: true })}
          {F('Criterios de éxito', 'success_criteria', 'textarea', { full: true })}
          {F('Riesgos / notas', 'risks_notes', 'textarea', { full: true })}
          <div className="col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Tags</label>
            <div className="flex gap-1 flex-wrap mb-1">
              {form.tags?.map(t => (
                <Badge key={t} small color="#8B1A1A">
                  {t} <button onClick={() => setForm({ ...form, tags: form.tags.filter(x => x !== t) })} className="ml-1">×</button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-1">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Agregar tag..." className="flex-1 bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none" />
              <button onClick={addTag} className="text-xs text-[#8B1A1A] px-2">+</button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-[#222]">
          <button onClick={onClose} className="px-4 py-1.5 text-sm text-gray-400 hover:text-white">Cancelar</button>
          <button onClick={handleSave} disabled={saving || !form.name || !form.objective}
            className="px-4 py-1.5 text-sm bg-[#8B1A1A] text-white rounded-lg hover:bg-[#a52020] disabled:opacity-50 flex items-center gap-1" data-testid="save-dynamic-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================= FLOWS TAB =============================
const FlowsTab = ({ onBack }) => {
  const [flows, setFlows] = useState([]);
  const [dynamics, setDynamics] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFlow, setEditingFlow] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [fRes, dRes, eRes] = await Promise.all([
        axios.get(`${API}/lab/flows`, { withCredentials: true }),
        axios.get(`${API}/lab/dynamics`, { withCredentials: true }),
        axios.get(`${API}/lab/events`, { withCredentials: true })
      ]);
      setFlows(fRes.data);
      setDynamics(dRes.data);
      setEvents(eRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (editingFlow || showCreate) {
    return <FlowEditor
      flow={editingFlow}
      dynamics={dynamics}
      events={events}
      onBack={() => { setEditingFlow(null); setShowCreate(false); fetchData(); }}
    />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 bg-[#8B1A1A] text-white px-3 py-1.5 rounded-lg text-sm hover:bg-[#a52020]" data-testid="create-flow-btn">
          <Plus size={14} /> Nuevo Flujo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8B1A1A]" size={24} /></div>
      ) : flows.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No hay flujos creados. Crea el primero para armar la secuencia de un evento.</div>
      ) : (
        <div className="space-y-2">
          {flows.map(f => (
            <div key={f.flow_id} className="bg-[#111] border border-[#222] rounded-lg p-4 hover:border-[#333] transition-colors" data-testid={`flow-${f.flow_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm">{f.name}</span>
                    {f.is_template && <Badge color="#8b5cf6" small>Plantilla</Badge>}
                    <Badge small color="#555">v{f.version || 1}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span><Layers size={12} className="inline mr-1" />{f.steps?.length || 0} pasos</span>
                    <span><Clock size={12} className="inline mr-1" />{f.total_duration_minutes || 0} min</span>
                    {f.event_id && <span><Calendar size={12} className="inline mr-1" />Asignado</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditingFlow(f)} className="p-1.5 text-gray-500 hover:text-white" title="Editar"><Edit size={14} /></button>
                  <button onClick={async () => { await axios.post(`${API}/lab/flows/${f.flow_id}/duplicate`, {}, { withCredentials: true }); fetchData(); }} className="p-1.5 text-gray-500 hover:text-white" title="Duplicar"><Copy size={14} /></button>
                </div>
              </div>
              {f.steps?.length > 0 && (
                <div className="mt-3 flex items-center gap-1 overflow-x-auto pb-1">
                  {f.steps.map((s, i) => (
                    <React.Fragment key={s.step_id || i}>
                      <div className="flex-shrink-0 bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-[10px] text-gray-400 max-w-[120px] truncate">
                        {s.dynamic?.name || s.dynamic_id?.slice(0, 10)}
                      </div>
                      {i < f.steps.length - 1 && <ChevronRight size={12} className="text-gray-700 flex-shrink-0" />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================= FLOW EDITOR =============================
const FlowEditor = ({ flow, dynamics, events, onBack }) => {
  const [name, setName] = useState(flow?.name || '');
  const [description, setDescription] = useState(flow?.description || '');
  const [isTemplate, setIsTemplate] = useState(flow?.is_template ?? true);
  const [eventId, setEventId] = useState(flow?.event_id || '');
  const [steps, setSteps] = useState(flow?.steps || []);
  const [notes, setNotes] = useState(flow?.notes || '');
  const [saving, setSaving] = useState(false);
  const [showDynamicPicker, setShowDynamicPicker] = useState(false);

  const addStep = (dynamic) => {
    setSteps([...steps, {
      step_id: `step_new_${Date.now()}`,
      dynamic_id: dynamic.dynamic_id,
      dynamic,
      order: steps.length + 1,
      duration_override: null,
      staff_notes: '',
      stage_label: dynamic.suggested_stage || 'interaccion',
      transition_notes: '',
      is_checkpoint: false
    }]);
    setShowDynamicPicker(false);
  };

  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));

  const moveStep = (idx, dir) => {
    const newSteps = [...steps];
    const target = idx + dir;
    if (target < 0 || target >= newSteps.length) return;
    [newSteps[idx], newSteps[target]] = [newSteps[target], newSteps[idx]];
    setSteps(newSteps);
  };

  const totalDuration = steps.reduce((sum, s) => sum + (s.duration_override || s.dynamic?.duration_minutes || 15), 0);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name, description, is_template: isTemplate,
        event_id: eventId || null, notes,
        steps: steps.map((s, i) => ({
          step_id: s.step_id,
          dynamic_id: s.dynamic_id,
          order: i + 1,
          duration_override: s.duration_override,
          staff_notes: s.staff_notes || '',
          stage_label: s.stage_label || '',
          transition_notes: s.transition_notes || '',
          is_checkpoint: s.is_checkpoint || false
        }))
      };
      if (flow?.flow_id) {
        await axios.put(`${API}/lab/flows/${flow.flow_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/lab/flows`, payload, { withCredentials: true });
      }
      onBack();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver a flujos</button>
        <button onClick={handleSave} disabled={saving || !name.trim()}
          className="flex items-center gap-1.5 bg-[#8B1A1A] text-white px-3 py-1.5 rounded-lg text-sm hover:bg-[#a52020] disabled:opacity-50" data-testid="save-flow-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar Flujo
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Nombre del flujo *</label>
          <input value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#111] border border-[#222] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#8B1A1A]" data-testid="flow-name-input" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Evento (opcional)</label>
          <select value={eventId} onChange={e => setEventId(e.target.value)} className="w-full bg-[#111] border border-[#222] rounded-lg px-3 py-2 text-sm text-white outline-none">
            <option value="">Sin evento (plantilla)</option>
            {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title} - {e.status}</option>)}
          </select>
        </div>
        <div className="col-span-full">
          <label className="block text-xs text-gray-500 mb-1">Descripción</label>
          <input value={description} onChange={e => setDescription(e.target.value)} className="w-full bg-[#111] border border-[#222] rounded-lg px-3 py-2 text-sm text-white outline-none" />
        </div>
      </div>

      <div className="bg-[#111] border border-[#222] rounded-lg p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-medium text-sm">Secuencia de Dinámicas</h3>
            <span className="text-xs text-gray-500">{steps.length} pasos</span>
            <span className="text-xs text-gray-500"><Clock size={12} className="inline mr-1" />{totalDuration} min total</span>
          </div>
          <button onClick={() => setShowDynamicPicker(true)}
            className="flex items-center gap-1 text-[#8B1A1A] text-sm hover:text-[#c52020]" data-testid="add-step-btn">
            <Plus size={14} /> Agregar dinámica
          </button>
        </div>

        {steps.length === 0 ? (
          <div className="text-center py-8 text-gray-600 border border-dashed border-[#333] rounded-lg">
            Agrega dinámicas de la biblioteca para construir el flujo
          </div>
        ) : (
          <div className="space-y-1">
            {steps.map((s, idx) => {
              const dyn = s.dynamic || dynamics.find(d => d.dynamic_id === s.dynamic_id);
              const cfg = CATEGORY_CONFIG[dyn?.category] || { label: '?', color: '#666' };
              const dur = s.duration_override || dyn?.duration_minutes || 15;
              return (
                <div key={s.step_id || idx} className="flex items-center gap-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-2" data-testid={`flow-step-${idx}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveStep(idx, -1)} className="text-gray-600 hover:text-white"><ChevronUp size={12} /></button>
                    <button onClick={() => moveStep(idx, 1)} className="text-gray-600 hover:text-white"><ChevronDown size={12} /></button>
                  </div>
                  <div className="w-6 h-6 rounded bg-[#222] flex items-center justify-center text-[10px] text-gray-500 font-bold">{idx + 1}</div>
                  <div className="w-1 h-6 rounded-full" style={{ backgroundColor: cfg.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{dyn?.name || s.dynamic_id}</div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <Badge small color={cfg.color}>{cfg.label}</Badge>
                      <span>{dur} min</span>
                    </div>
                  </div>
                  <input type="number" value={s.duration_override || ''} onChange={e => {
                    const newSteps = [...steps];
                    newSteps[idx] = { ...s, duration_override: parseInt(e.target.value) || null };
                    setSteps(newSteps);
                  }} placeholder={`${dyn?.duration_minutes || 15}`} className="w-14 bg-[#111] border border-[#222] rounded px-1 py-0.5 text-xs text-center text-white outline-none" title="Override duración (min)" />
                  <button onClick={() => removeStep(idx)} className="text-gray-600 hover:text-red-500 p-1"><X size={14} /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showDynamicPicker && <DynamicPicker dynamics={dynamics} onSelect={addStep} onClose={() => setShowDynamicPicker(false)} />}
    </div>
  );
};

// ============================= DYNAMIC PICKER MODAL =============================
const DynamicPicker = ({ dynamics, onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const filtered = dynamics.filter(d =>
    (!catFilter || d.category === catFilter) &&
    (!search || d.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4 pt-10" data-testid="dynamic-picker-modal">
      <div className="bg-[#0d0d0d] border border-[#222] rounded-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-3 border-b border-[#222]">
          <h3 className="text-white font-medium text-sm">Seleccionar Dinámica</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-3 space-y-2">
          <div className="flex gap-2">
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..."
              className="flex-1 bg-[#111] border border-[#222] rounded px-2 py-1.5 text-sm text-white outline-none" autoFocus />
            <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-sm text-white outline-none">
              <option value="">Todas</option>
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-1">
            {filtered.map(d => {
              const cfg = CATEGORY_CONFIG[d.category] || { label: d.category, color: '#666' };
              return (
                <button key={d.dynamic_id} onClick={() => onSelect(d)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg bg-[#111] border border-[#222] hover:border-[#8B1A1A] text-left transition-colors">
                  <div className="w-2 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{d.name}</div>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <Badge small color={cfg.color}>{cfg.label}</Badge>
                      <span>{d.duration_minutes}min</span>
                      <span>{STAGE_LABELS[d.suggested_stage] || d.suggested_stage}</span>
                    </div>
                  </div>
                  <Plus size={14} className="text-gray-600" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================= EVENTS TAB =============================
const EventsTab = ({ onBack }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrichingId, setEnrichingId] = useState(null);
  const [enrichForm, setEnrichForm] = useState({});

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lab/events`, { withCredentials: true });
      setEvents(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const saveEnrichment = async (eventId) => {
    try {
      await axios.put(`${API}/lab/events/${eventId}/enrich`, enrichForm, { withCredentials: true });
      setEnrichingId(null);
      fetchEvents();
    } catch (e) { console.error(e); }
  };

  const startEnrich = (evt) => {
    setEnrichForm({
      is_pilot: evt.is_pilot ?? true,
      pilot_version: evt.pilot_version || '1.0',
      hypothesis: evt.hypothesis || '',
      target_metrics: evt.target_metrics || [],
      event_format: evt.event_format || 'premium',
      internal_responsible: evt.internal_responsible || '',
      operational_notes: evt.operational_notes || '',
      selection_method: evt.selection_method || 'hibrido'
    });
    setEnrichingId(evt.event_id);
  };

  const STATUS_COLORS = {
    draft: '#666', inviting: '#f59e0b', confirmed: '#22c55e', in_progress: '#3b82f6', completed: '#06b6d4', cancelled: '#ef4444'
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8B1A1A]" size={24} /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No hay eventos. Crea uno desde el panel de administración.</div>
      ) : (
        <div className="space-y-2">
          {events.map(evt => (
            <div key={evt.event_id} className="bg-[#111] border border-[#222] rounded-lg p-4" data-testid={`event-${evt.event_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{evt.title}</span>
                    <Badge color={STATUS_COLORS[evt.status] || '#666'}>{evt.status}</Badge>
                    {evt.is_pilot && <Badge color="#f59e0b" small>Piloto v{evt.pilot_version || '?'}</Badge>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{evt.city}</span>
                    <span>{evt.date ? new Date(evt.date).toLocaleDateString('es-ES') : '—'}</span>
                    <span><Users size={12} className="inline mr-1" />{evt.attendance_real}/{evt.confirmed_count} asistieron</span>
                    <span><Heart size={12} className="inline mr-1" />{evt.matches_count} matches</span>
                    <span><Layers size={12} className="inline mr-1" />{evt.dynamics_count} dinámicas</span>
                  </div>
                  {evt.hypothesis && <div className="mt-2 text-xs text-gray-400 italic">Hipótesis: {evt.hypothesis}</div>}
                </div>
                <button onClick={() => startEnrich(evt)} className="text-xs text-[#8B1A1A] hover:text-[#c52020] flex items-center gap-1" data-testid={`enrich-${evt.event_id}`}>
                  <Edit size={12} /> Enriquecer
                </button>
              </div>

              {enrichingId === evt.event_id && (
                <div className="mt-3 border-t border-[#222] pt-3 grid grid-cols-2 gap-2">
                  <label className="flex items-center gap-2 text-sm text-gray-400 col-span-2">
                    <input type="checkbox" checked={enrichForm.is_pilot} onChange={e => setEnrichForm({ ...enrichForm, is_pilot: e.target.checked })} className="accent-[#8B1A1A]" />
                    Evento Piloto
                  </label>
                  <div>
                    <label className="text-[10px] text-gray-500">Versión piloto</label>
                    <input value={enrichForm.pilot_version} onChange={e => setEnrichForm({ ...enrichForm, pilot_version: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Formato</label>
                    <select value={enrichForm.event_format} onChange={e => setEnrichForm({ ...enrichForm, event_format: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none">
                      {['intimo', 'premium', 'grupal', 'tematico', 'experimental'].map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Método de selección</label>
                    <select value={enrichForm.selection_method} onChange={e => setEnrichForm({ ...enrichForm, selection_method: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none">
                      {['manual', 'algoritmo', 'hibrido'].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">Responsable interno</label>
                    <input value={enrichForm.internal_responsible} onChange={e => setEnrichForm({ ...enrichForm, internal_responsible: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">Hipótesis a probar</label>
                    <textarea value={enrichForm.hypothesis} onChange={e => setEnrichForm({ ...enrichForm, hypothesis: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none resize-none" rows={2} />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">Notas operativas</label>
                    <textarea value={enrichForm.operational_notes} onChange={e => setEnrichForm({ ...enrichForm, operational_notes: e.target.value })} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1 text-xs text-white outline-none resize-none" rows={2} />
                  </div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <button onClick={() => setEnrichingId(null)} className="text-xs text-gray-500 px-3 py-1">Cancelar</button>
                    <button onClick={() => saveEnrichment(evt.event_id)} className="text-xs bg-[#8B1A1A] text-white px-3 py-1 rounded hover:bg-[#a52020]" data-testid="save-enrichment-btn">Guardar</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================= JOURNEYS TAB (Pipeline View) =============================
const STAGE_CONFIG = {
  initial_interest: { label: 'Interés Inicial', color: '#6b7280', order: 0 },
  mutual_selection: { label: 'Selección Mutua', color: '#22c55e', order: 1 },
  affinity_detected: { label: 'Afinidad Detectada', color: '#3b82f6', order: 2 },
  followup_suggested: { label: 'Seguimiento', color: '#8b5cf6', order: 3 },
  invited_to_second_event: { label: 'Invitados 2do Evento', color: '#f59e0b', order: 4 },
  priority_connection: { label: 'Conexión Prioritaria', color: '#ec4899', order: 5 },
  facilitated_date: { label: 'Cita Facilitada', color: '#ef4444', order: 6 },
  observed_bond: { label: 'Vínculo Observado', color: '#8B1A1A', order: 7 },
  closed_success: { label: 'Cerrado Exitoso', color: '#10b981', order: 8 },
  closed_inactive: { label: 'Cerrado Inactivo', color: '#374151', order: 9 }
};

const REC_CONFIG = {
  invite_event: { icon: Calendar, color: '#f59e0b', bg: '#f59e0b' },
  prioritize: { icon: Star, color: '#ec4899', bg: '#ec4899' },
  facilitate: { icon: Heart, color: '#8B1A1A', bg: '#8B1A1A' },
  observe: { icon: Eye, color: '#6b7280', bg: '#6b7280' },
  discard: { icon: X, color: '#374151', bg: '#374151' },
  archived: { icon: Archive, color: '#374151', bg: '#374151' }
};

const URGENCY_COLORS = { high: '#ef4444', medium: '#f59e0b', low: '#6b7280', none: '#374151' };

const AffinityBar = ({ score, max = 50 }) => {
  const pct = Math.min(Math.max((score / max) * 100, 0), 100);
  const color = score >= 15 ? '#8B1A1A' : score >= 8 ? '#f59e0b' : score >= 3 ? '#6b7280' : '#222';
  return (
    <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

const JourneyCard = ({ j, onOpen, onAdvance, onPriority }) => {
  const stageCfg = STAGE_CONFIG[j.current_stage] || { label: j.current_stage, color: '#666' };
  const rec = j.recommendation || {};
  const recCfg = REC_CONFIG[rec.action] || REC_CONFIG.observe;
  const RecIcon = recCfg.icon;
  const isHighAffinity = j.affinity_score_accumulated >= 10;
  const isFollowupCandidate = j.mutual_selection_count >= 1 && j.current_stage !== 'closed_inactive';

  return (
    <div className={`bg-[#111] border rounded-lg p-3 hover:border-[#444] transition-all cursor-pointer group ${
      isHighAffinity ? 'border-[#8B1A1A]/40' : isFollowupCandidate ? 'border-[#22c55e]/30' : 'border-[#222]'
    }`} onClick={() => onOpen(j.journey_id)} data-testid={`journey-card-${j.journey_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: stageCfg.color + '20' }}>
            <GitBranch size={14} style={{ color: stageCfg.color }} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-white truncate max-w-[120px]">{j.user_a?.name || '?'}</span>
            <span className="text-[10px] text-gray-600">&amp;</span>
            <span className="text-sm font-medium text-white truncate max-w-[120px]">{j.user_b?.name || '?'}</span>
            {isHighAffinity && <span className="w-1.5 h-1.5 rounded-full bg-[#8B1A1A] flex-shrink-0" title="Alta afinidad" />}
            {j.admin_priority_level > 0 && <Badge small color="#f59e0b">P{j.admin_priority_level}</Badge>}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] text-gray-500 font-mono">{j.affinity_score_accumulated}pts</span>
            <span className="text-[10px] text-gray-600">|</span>
            <span className="text-[10px] text-gray-500">{j.mutual_selection_count} mutua{j.mutual_selection_count !== 1 ? 's' : ''}</span>
            <span className="text-[10px] text-gray-600">|</span>
            <span className="text-[10px] text-gray-500">{j.shared_events_count} evento{j.shared_events_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="mt-1.5 w-24"><AffinityBar score={j.affinity_score_accumulated} /></div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px]" style={{ backgroundColor: recCfg.bg + '15', color: recCfg.color, border: `1px solid ${recCfg.bg}30` }}>
            <RecIcon size={10} />
            <span>{rec.label || 'Observar'}</span>
          </div>
          {rec.urgency && rec.urgency !== 'none' && (
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: URGENCY_COLORS[rec.urgency] }} title={`Urgencia: ${rec.urgency}`} />
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={e => { e.stopPropagation(); onAdvance(j.journey_id); }}
          className="text-[10px] text-gray-600 hover:text-green-400 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#0a0a0a]" data-testid={`quick-advance-${j.journey_id}`}>
          <ArrowUpRight size={10} /> Avanzar
        </button>
        <button onClick={e => { e.stopPropagation(); onPriority(j.journey_id, j.admin_priority_level >= 3 ? 0 : j.admin_priority_level + 1); }}
          className="text-[10px] text-gray-600 hover:text-yellow-400 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#0a0a0a]">
          <Star size={10} /> Prioridad
        </button>
      </div>
    </div>
  );
};

const JourneysTab = ({ onBack }) => {
  const [journeys, setJourneys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('');
  const [minAffinity, setMinAffinity] = useState('');
  const [minEvents, setMinEvents] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [sortBy, setSortBy] = useState('affinity');
  const [viewMode, setViewMode] = useState('pipeline');
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [detailData, setDetailData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sort_by: sortBy, limit: '200' });
      if (stageFilter) params.set('stage', stageFilter);
      if (minAffinity) params.set('min_affinity', minAffinity);
      if (minEvents) params.set('min_events', minEvents);
      if (priorityFilter) params.set('priority', priorityFilter);
      const [jRes, sRes] = await Promise.all([
        axios.get(`${API}/journeys?${params}`, { withCredentials: true }),
        axios.get(`${API}/journeys/stats`, { withCredentials: true })
      ]);
      setJourneys(jRes.data);
      setStats(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [stageFilter, sortBy, minAffinity, minEvents, priorityFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (journeyId) => {
    try {
      const { data } = await axios.get(`${API}/journeys/${journeyId}`, { withCredentials: true });
      setDetailData(data);
      setSelectedJourney(journeyId);
    } catch (e) { console.error(e); }
  };

  const advanceStage = async (journeyId, targetStage) => {
    try {
      await axios.post(`${API}/journeys/${journeyId}/advance-stage`,
        targetStage ? { target_stage: targetStage } : {},
        { withCredentials: true }
      );
      fetchData();
      if (selectedJourney === journeyId) openDetail(journeyId);
    } catch (e) { console.error(e); }
  };

  const setPriority = async (journeyId, priority) => {
    try {
      await axios.post(`${API}/journeys/${journeyId}/set-priority`, { priority }, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const closeJourney = async (journeyId, outcome) => {
    try {
      await axios.post(`${API}/journeys/${journeyId}/close`, { outcome }, { withCredentials: true });
      fetchData();
      setSelectedJourney(null);
    } catch (e) { console.error(e); }
  };

  if (selectedJourney && detailData) {
    return <JourneyDetail data={detailData} onBack={() => { setSelectedJourney(null); setDetailData(null); }}
      onAdvance={(target) => advanceStage(selectedJourney, target)}
      onClose={(outcome) => closeJourney(selectedJourney, outcome)}
      onRefresh={() => openDetail(selectedJourney)} />;
  }

  // Group journeys by stage for pipeline view
  const grouped = {};
  const stageOrder = Object.keys(STAGE_CONFIG);
  stageOrder.forEach(s => { grouped[s] = []; });
  journeys.forEach(j => {
    const s = j.current_stage || 'initial_interest';
    if (grouped[s]) grouped[s].push(j);
  });

  const activeStages = stageOrder.filter(s => (grouped[s]?.length > 0) || (stats?.by_stage?.[s] > 0));
  const hasFilters = stageFilter || minAffinity || minEvents || priorityFilter;

  return (
    <div className="space-y-4" data-testid="journeys-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <div className="flex items-center gap-2">
          <button onClick={() => setViewMode(viewMode === 'pipeline' ? 'list' : 'pipeline')}
            className={`text-xs px-2 py-1 rounded border ${viewMode === 'pipeline' ? 'border-[#8B1A1A] text-[#8B1A1A]' : 'border-[#333] text-gray-500'}`}
            data-testid="toggle-view-mode">
            {viewMode === 'pipeline' ? 'Pipeline' : 'Lista'}
          </button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'Total Journeys', value: stats.total_journeys, icon: GitBranch, color: '#fff' },
            { label: 'Selecciones Mutuas', value: stats.total_mutual_selections, icon: Heart, color: '#8B1A1A' },
            { label: 'Afinidad Promedio', value: stats.avg_affinity_score, icon: Activity, color: '#f59e0b' },
            { label: 'Afinidad Máxima', value: stats.max_affinity_score, icon: Zap, color: '#22c55e' }
          ].map((c, i) => (
            <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <c.icon size={12} style={{ color: c.color }} />
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">{c.label}</span>
              </div>
              <div className="text-xl font-bold text-white">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="sort-journeys">
          <option value="affinity">Afinidad</option>
          <option value="updated">Reciente</option>
        </select>
        <input type="number" placeholder="Min afinidad" value={minAffinity} onChange={e => setMinAffinity(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none w-28" data-testid="filter-min-affinity" />
        <input type="number" placeholder="Min eventos" value={minEvents} onChange={e => setMinEvents(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none w-28" data-testid="filter-min-events" />
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="filter-priority">
          <option value="">Toda prioridad</option>
          <option value="1">P1+</option>
          <option value="2">P2+</option>
          <option value="3">P3</option>
        </select>
        {hasFilters && (
          <button onClick={() => { setStageFilter(''); setMinAffinity(''); setMinEvents(''); setPriorityFilter(''); }}
            className="text-[10px] text-gray-500 hover:text-white px-2 py-1 border border-[#333] rounded">Limpiar</button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8B1A1A]" size={28} /></div>
      ) : journeys.length === 0 ? (
        <div className="text-center py-16">
          <GitBranch size={32} className="text-gray-700 mx-auto mb-3" />
          <div className="text-gray-500 text-sm">No hay journeys{hasFilters ? ' con estos filtros' : ''}.</div>
          <div className="text-gray-600 text-xs mt-1">Se crean automáticamente al procesar selecciones de eventos.</div>
        </div>
      ) : viewMode === 'pipeline' ? (
        /* ===== PIPELINE VIEW ===== */
        <div className="space-y-4">
          {activeStages.filter(s => !stageFilter || s === stageFilter).map(stageKey => {
            const cfg = STAGE_CONFIG[stageKey];
            const stageJourneys = grouped[stageKey] || [];
            if (stageJourneys.length === 0) return null;
            const highUrgency = stageJourneys.filter(j => j.recommendation?.urgency === 'high');
            return (
              <div key={stageKey} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl overflow-hidden" data-testid={`stage-section-${stageKey}`}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }} />
                    <span className="text-sm font-medium text-white">{cfg.label}</span>
                    <span className="text-xs text-gray-500 font-mono bg-[#111] px-1.5 rounded">{stageJourneys.length}</span>
                    {highUrgency.length > 0 && (
                      <span className="text-[10px] text-red-400 flex items-center gap-0.5">
                        <AlertTriangle size={10} /> {highUrgency.length} urgente{highUrgency.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setStageFilter(stageFilter === stageKey ? '' : stageKey)}
                    className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                      stageFilter === stageKey ? 'border-[#8B1A1A] text-[#8B1A1A]' : 'border-[#333] text-gray-600 hover:text-gray-400'
                    }`}><Filter size={10} className="inline mr-0.5" />Filtrar</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-3">
                  {stageJourneys.map(j => (
                    <JourneyCard key={j.journey_id} j={j} onOpen={openDetail} onAdvance={(id) => advanceStage(id)} onPriority={setPriority} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* ===== LIST VIEW ===== */
        <div className="space-y-1.5">
          {journeys.map(j => (
            <JourneyCard key={j.journey_id} j={j} onOpen={openDetail} onAdvance={(id) => advanceStage(id)} onPriority={setPriority} />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================= JOURNEY DETAIL =============================
const JourneyDetail = ({ data, onBack, onAdvance, onClose }) => {
  const stageCfg = STAGE_CONFIG[data.current_stage] || { label: data.current_stage, color: '#666' };
  const rec = data.recommendation || {};
  const recCfg = REC_CONFIG[rec.action] || REC_CONFIG.observe;
  const RecIcon = recCfg.icon;

  return (
    <div className="space-y-4" data-testid="journey-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm">
        <ArrowLeft size={16} /> Volver a journeys
      </button>

      {/* Header */}
      <div className="bg-[#111] border border-[#222] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#8B1A1A]/20 flex items-center justify-center mx-auto mb-1">
                <Users size={18} className="text-[#8B1A1A]" />
              </div>
              <div className="text-white font-medium text-sm">{data.user_a?.name || '?'}</div>
              <div className="text-[10px] text-gray-500">{data.user_a?.occupation || data.user_a?.email || ''}</div>
            </div>
            <div className="text-center px-4">
              <div className="text-2xl font-bold" style={{ color: stageCfg.color }}>{data.affinity_score_accumulated}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">AFINIDAD</div>
              <AffinityBar score={data.affinity_score_accumulated} />
            </div>
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-[#8B1A1A]/20 flex items-center justify-center mx-auto mb-1">
                <Users size={18} className="text-[#8B1A1A]" />
              </div>
              <div className="text-white font-medium text-sm">{data.user_b?.name || '?'}</div>
              <div className="text-[10px] text-gray-500">{data.user_b?.occupation || data.user_b?.email || ''}</div>
            </div>
          </div>
          <Badge color={stageCfg.color}>{stageCfg.label}</Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
          {[
            { label: 'Selecciones Mutuas', value: data.mutual_selection_count, icon: Heart },
            { label: 'Eventos Compartidos', value: data.shared_events_count, icon: Calendar },
            { label: 'Interacciones', value: data.shared_interactions_count, icon: MessageCircle },
            { label: 'Positivas', value: data.positive_interactions, icon: CheckCircle }
          ].map((m, i) => (
            <div key={i} className="bg-[#0a0a0a] rounded-lg p-2 text-center">
              <m.icon size={14} className="text-gray-600 mx-auto mb-1" />
              <div className="text-lg font-bold text-white">{m.value}</div>
              <div className="text-[10px] text-gray-500">{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendation */}
      <div className="bg-[#111] border rounded-lg p-4" style={{ borderColor: recCfg.bg + '40' }} data-testid="journey-recommendation">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: recCfg.bg + '20' }}>
            <RecIcon size={16} style={{ color: recCfg.color }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-white">Acción Recomendada: {rec.label}</span>
              {rec.urgency && rec.urgency !== 'none' && (
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: URGENCY_COLORS[rec.urgency] + '20', color: URGENCY_COLORS[rec.urgency] }}>
                  {rec.urgency === 'high' ? 'Urgente' : rec.urgency === 'medium' ? 'Media' : 'Baja'}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400">{rec.detail}</p>
            <ExplainPanel rec={rec} />
          </div>
        </div>
      </div>
      <div className="bg-[#111] border border-[#222] rounded-lg p-4">
        <h3 className="text-white font-medium text-sm mb-2">Pipeline de Etapas</h3>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {Object.entries(STAGE_CONFIG).slice(0, 8).map(([key, cfg], idx) => {
            const isCurrent = key === data.current_stage;
            const isPast = cfg.order < (STAGE_CONFIG[data.current_stage]?.order || 0);
            return (
              <React.Fragment key={key}>
                <div className={`flex-shrink-0 px-2 py-1 rounded text-[10px] border transition-all cursor-pointer ${
                  isCurrent ? 'border-white bg-white/10 text-white font-bold' :
                  isPast ? 'border-transparent bg-[#0a0a0a] text-gray-400' :
                  'border-[#222] text-gray-600'
                }`} onClick={() => !isPast && onAdvance(key)} title={`Mover a: ${cfg.label}`}>
                  <div className="w-2 h-2 rounded-full mx-auto mb-0.5" style={{ backgroundColor: isCurrent || isPast ? cfg.color : '#333' }} />
                  {cfg.label}
                </div>
                {idx < 7 && <ChevronRight size={10} className="text-gray-700 flex-shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Stage History */}
      {data.stage_history?.length > 0 && (
        <div className="bg-[#111] border border-[#222] rounded-lg p-4">
          <h3 className="text-white font-medium text-sm mb-2">Historial de Cambios</h3>
          <div className="space-y-1.5">
            {data.stage_history.map((h, i) => {
              const fromCfg = STAGE_CONFIG[h.from_stage] || {};
              const toCfg = STAGE_CONFIG[h.to_stage] || {};
              return (
                <div key={h.history_id || i} className="flex items-center gap-2 text-xs">
                  <span className="text-gray-600 w-28 flex-shrink-0">{new Date(h.timestamp).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  <Badge small color={fromCfg.color || '#666'}>{fromCfg.label || h.from_stage}</Badge>
                  <ChevronRight size={10} className="text-gray-600" />
                  <Badge small color={toCfg.color || '#666'}>{toCfg.label || h.to_stage}</Badge>
                  <span className="text-gray-600">({h.reason})</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Admin Actions */}
      <div className="bg-[#111] border border-[#222] rounded-lg p-4">
        <h3 className="text-white font-medium text-sm mb-2">Acciones Admin</h3>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => onAdvance(null)} className="px-3 py-1.5 bg-green-900/30 border border-green-800/50 text-green-400 rounded text-xs hover:bg-green-900/50" data-testid="advance-next-btn">
            <ArrowUpRight size={12} className="inline mr-1" /> Avanzar Etapa
          </button>
          <button onClick={() => onClose('closed_success')} className="px-3 py-1.5 bg-[#8B1A1A]/20 border border-[#8B1A1A]/50 text-[#8B1A1A] rounded text-xs hover:bg-[#8B1A1A]/30" data-testid="close-success-btn">
            <CheckCircle size={12} className="inline mr-1" /> Cerrar Exitoso
          </button>
          <button onClick={() => onClose('closed_inactive')} className="px-3 py-1.5 bg-gray-800/50 border border-gray-700 text-gray-400 rounded text-xs hover:bg-gray-800">
            <X size={12} className="inline mr-1" /> Cerrar Inactivo
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================= INCIDENTS TAB (Sprint 3) =============================
const INCIDENT_CATEGORIES = [
  { value: 'dynamic_failure', label: 'Falla de Dinámica', color: '#ef4444' },
  { value: 'interaction_friction', label: 'Fricción de Interacción', color: '#f59e0b' },
  { value: 'emotional_friction', label: 'Fricción Emocional', color: '#ec4899' },
  { value: 'matching_problem', label: 'Problema de Matching', color: '#8b5cf6' },
  { value: 'timing_issue', label: 'Problema de Timing', color: '#3b82f6' }
];
const SEVERITY_OPTS = [
  { value: 'low', label: 'Baja', color: '#6b7280' },
  { value: 'medium', label: 'Media', color: '#f59e0b' },
  { value: 'high', label: 'Alta', color: '#ef4444' },
  { value: 'critical', label: 'Crítica', color: '#8B1A1A' }
];

const IncidentsTab = ({ onBack }) => {
  const [incidents, setIncidents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filterCat, setFilterCat] = useState('');
  const [filterSev, setFilterSev] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({
    event_id: '', dynamic_id: '', journey_id: '', category: 'dynamic_failure',
    severity: 'medium', description: '', impact_area: 'interaction_quality',
    impact_weight: 2, source: 'staff', evidence: {}
  });
  const [events, setEvents] = useState([]);
  const [dynamics, setDynamics] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCat) params.set('category', filterCat);
      if (filterSev) params.set('severity', filterSev);
      if (filterStatus) params.set('status', filterStatus);
      const [iRes, sRes, eRes, dRes] = await Promise.all([
        axios.get(`${API}/incidents?${params}`, { withCredentials: true }),
        axios.get(`${API}/incidents/summary`, { withCredentials: true }),
        axios.get(`${API}/admin/events`, { withCredentials: true }),
        axios.get(`${API}/lab/dynamics`, { withCredentials: true })
      ]);
      setIncidents(iRes.data);
      setSummary(sRes.data);
      setEvents(eRes.data?.slice?.(0, 20) || []);
      setDynamics(dRes.data?.slice?.(0, 50) || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterCat, filterSev, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createIncident = async () => {
    try {
      await axios.post(`${API}/incidents`, form, { withCredentials: true });
      setShowForm(false);
      setForm(f => ({ ...f, description: '', evidence: {} }));
      fetchData();
    } catch (e) { console.error(e); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/incidents/${id}`, { resolution_status: status }, { withCredentials: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const catCfg = (cat) => INCIDENT_CATEGORIES.find(c => c.value === cat) || { label: cat, color: '#666' };
  const sevCfg = (sev) => SEVERITY_OPTS.find(s => s.value === sev) || { label: sev, color: '#666' };

  return (
    <div className="space-y-4" data-testid="incidents-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-[#8B1A1A] text-white rounded text-xs hover:bg-[#8B1A1A]/80" data-testid="new-incident-btn">
          <Plus size={14} /> Reportar Incidencia
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-white">{summary.total}</div>
            <div className="text-[10px] text-gray-500">Total</div>
          </div>
          <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-red-400">{summary.open}</div>
            <div className="text-[10px] text-gray-500">Abiertas</div>
          </div>
          <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-green-400">{summary.resolved}</div>
            <div className="text-[10px] text-gray-500">Resueltas</div>
          </div>
          <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-white">{summary.resolution_rate}%</div>
            <div className="text-[10px] text-gray-500">Tasa Resolución</div>
          </div>
          <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-[#8B1A1A]">{(summary.by_severity?.critical || 0) + (summary.by_severity?.high || 0)}</div>
            <div className="text-[10px] text-gray-500">Alta/Crítica</div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-[#111] border border-[#8B1A1A]/30 rounded-xl p-4 space-y-3" data-testid="incident-form">
          <h3 className="text-white font-medium text-sm">Nueva Incidencia</h3>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.event_id} onChange={e => setForm(f => ({ ...f, event_id: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="form-event">
              <option value="">Evento (opcional)</option>
              {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title || e.event_id}</option>)}
            </select>
            <select value={form.dynamic_id} onChange={e => setForm(f => ({ ...f, dynamic_id: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="form-dynamic">
              <option value="">Dinámica (opcional)</option>
              {dynamics.map(d => <option key={d.dynamic_id} value={d.dynamic_id}>{d.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="form-category">
              {INCIDENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="form-severity">
              {SEVERITY_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="form-source">
              <option value="staff">Staff</option>
              <option value="participant">Participante</option>
              <option value="system">Sistema</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={form.impact_area} onChange={e => setForm(f => ({ ...f, impact_area: e.target.value }))}
              className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none">
              {['interaction_quality', 'selection_accuracy', 'emotional_safety', 'stage_progression', 'event_flow', 'participant_retention'].map(a => (
                <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-gray-500">Peso:</span>
              {[1,2,3,4,5].map(w => (
                <button key={w} onClick={() => setForm(f => ({ ...f, impact_weight: w }))}
                  className={`w-6 h-6 rounded text-xs ${form.impact_weight === w ? 'bg-[#8B1A1A] text-white' : 'bg-[#0a0a0a] text-gray-500 border border-[#222]'}`}>{w}</button>
              ))}
            </div>
          </div>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Descripción de la incidencia..." rows={2}
            className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none resize-none" data-testid="form-description" />
          <div className="flex gap-2">
            <button onClick={createIncident} className="px-3 py-1.5 bg-[#8B1A1A] text-white rounded text-xs" data-testid="submit-incident">Crear</button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-[#222] text-gray-400 rounded text-xs">Cancelar</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none">
          <option value="">Toda categoría</option>
          {INCIDENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none">
          <option value="">Toda severidad</option>
          {SEVERITY_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none">
          <option value="">Todo status</option>
          {['open', 'investigating', 'resolved', 'dismissed'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#8B1A1A]" size={24} /></div> :
      incidents.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm">No hay incidencias registradas.</div> :
      <div className="space-y-1.5">
        {incidents.map(inc => {
          const cc = catCfg(inc.category);
          const sc = sevCfg(inc.severity);
          return (
            <div key={inc.incident_id} className="bg-[#111] border border-[#222] rounded-lg p-3 hover:border-[#333] transition-colors" data-testid={`incident-${inc.incident_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: cc.color + '20', color: cc.color, border: `1px solid ${cc.color}30` }}>{cc.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: sc.color + '20', color: sc.color }}>{sc.label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${inc.resolution_status === 'open' ? 'bg-red-900/20 text-red-400' : inc.resolution_status === 'resolved' ? 'bg-green-900/20 text-green-400' : 'bg-gray-800 text-gray-400'}`}>{inc.resolution_status}</span>
                    <span className="text-[10px] text-gray-600">peso: {inc.impact_weight}/5</span>
                    <span className="text-[10px] text-gray-600">{inc.source}</span>
                  </div>
                  <p className="text-sm text-white">{inc.description}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-500">
                    {inc.event_id && <span>Evento: {inc.event_id.slice(-8)}</span>}
                    {inc.dynamic_id && <span>Dinámica: {inc.dynamic_id}</span>}
                    <span>{new Date(inc.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
                <div className="flex gap-1 ml-2">
                  {inc.resolution_status === 'open' && (
                    <>
                      <button onClick={() => updateStatus(inc.incident_id, 'investigating')} className="text-[10px] px-1.5 py-0.5 border border-[#333] text-gray-500 rounded hover:text-yellow-400">Investigar</button>
                      <button onClick={() => updateStatus(inc.incident_id, 'resolved')} className="text-[10px] px-1.5 py-0.5 border border-[#333] text-gray-500 rounded hover:text-green-400">Resolver</button>
                    </>
                  )}
                  {inc.resolution_status === 'investigating' && (
                    <button onClick={() => updateStatus(inc.incident_id, 'resolved')} className="text-[10px] px-1.5 py-0.5 border border-green-800 text-green-400 rounded hover:bg-green-900/20">Resolver</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>}
    </div>
  );
};


// ============================= METRICS TAB (Sprint 3) =============================
const ConfidenceBadge = ({ confidence }) => {
  if (!confidence) return null;
  const colors = { low: '#6b7280', medium: '#f59e0b', high: '#22c55e' };
  const labels = { low: 'Baja', medium: 'Media', high: 'Alta' };
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid="confidence-badge"
      style={{ backgroundColor: (colors[confidence.level] || '#666') + '15', color: colors[confidence.level] || '#666', border: `1px solid ${colors[confidence.level] || '#666'}30` }}>
      <Gauge size={9} /> Confianza: {labels[confidence.level] || confidence.level}
    </span>
  );
};

const TrendIcon = ({ trend }) => {
  if (trend === 'improving') return <TrendingUp size={12} className="text-green-400" />;
  if (trend === 'declining') return <TrendingDown size={12} className="text-red-400" />;
  if (trend === 'stable') return <Minus size={12} className="text-gray-500" />;
  return <Minus size={12} className="text-gray-600" />;
};

const REC_ACTIONS_DYNAMIC = {
  use_more: { label: 'Usar más', color: '#22c55e', icon: TrendingUp },
  adjust: { label: 'Ajustar', color: '#f59e0b', icon: Edit },
  retire: { label: 'Retirar', color: '#ef4444', icon: X },
  observe: { label: 'Observar', color: '#6b7280', icon: Eye }
};

const MetricsTab = ({ onBack }) => {
  const [view, setView] = useState('system');
  const [systemData, setSystemData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [dynamicsRanking, setDynamicsRanking] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [sRes, eRes] = await Promise.all([
          axios.get(`${API}/metrics/system`, { withCredentials: true }),
          axios.get(`${API}/admin/events`, { withCredentials: true })
        ]);
        setSystemData(sRes.data);
        setEvents(eRes.data?.slice?.(0, 20) || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const loadEventMetrics = async (eid) => {
    setSelectedEvent(eid);
    if (!eid) { setEventData(null); return; }
    try {
      const { data } = await axios.get(`${API}/metrics/event/${eid}`, { withCredentials: true });
      setEventData(data);
    } catch (e) { console.error(e); }
  };

  const loadDynamicsRanking = async () => {
    try {
      const { data } = await axios.get(`${API}/metrics/dynamics/ranking`, { withCredentials: true });
      setDynamicsRanking(data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (view === 'dynamics') loadDynamicsRanking(); }, [view]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8B1A1A]" size={28} /></div>;

  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <div className="flex items-center gap-1 bg-[#111] border border-[#222] rounded-lg p-0.5">
          {['system', 'event', 'dynamics'].map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1 rounded text-xs transition-colors ${view === v ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' : 'text-gray-500'}`}
              data-testid={`metrics-view-${v}`}>
              {v === 'system' ? 'Sistema' : v === 'event' ? 'Evento' : 'Dinámicas'}
            </button>
          ))}
        </div>
      </div>

      {view === 'system' && systemData && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-medium">Métricas del Sistema</h3>
            <ConfidenceBadge confidence={systemData.confidence} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { label: '% Avanzando', value: `${systemData.journeys.pct_advancing}%`, color: '#22c55e' },
              { label: '% Estancados', value: `${systemData.journeys.pct_stagnated}%`, color: systemData.journeys.pct_stagnated > 30 ? '#ef4444' : '#6b7280' },
              { label: 'Journeys Activos', value: systemData.journeys.active, color: '#fff' },
              { label: 'Eventos Completados', value: systemData.events.completed, color: '#3b82f6' },
              { label: 'Participantes', value: systemData.participants.unique, color: '#f59e0b' },
              { label: 'Interacciones', value: systemData.interactions.total, color: '#8b5cf6' },
              { label: 'Incidencias Abiertas', value: systemData.incidents.open, color: systemData.incidents.open > 0 ? '#ef4444' : '#22c55e' },
              { label: 'Total Journeys', value: systemData.journeys.total, color: '#fff' },
            ].map((m, i) => (
              <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-3 text-center">
                <div className="text-xl font-bold" style={{ color: m.color }}>{m.value}</div>
                <div className="text-[10px] text-gray-500">{m.label}</div>
              </div>
            ))}
          </div>
          {systemData.journeys.stage_distribution && Object.keys(systemData.journeys.stage_distribution).length > 0 && (
            <div className="bg-[#111] border border-[#222] rounded-lg p-4">
              <h4 className="text-sm text-white font-medium mb-2">Distribución por Etapa</h4>
              <div className="space-y-1.5">
                {Object.entries(systemData.journeys.stage_distribution).map(([stage, count]) => {
                  const cfg = STAGE_CONFIG[stage] || { label: stage, color: '#666' };
                  const pct = Math.round(count / Math.max(systemData.journeys.active, 1) * 100);
                  return (
                    <div key={stage} className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: cfg.color }} />
                      <span className="text-xs text-gray-400 w-36">{cfg.label}</span>
                      <div className="flex-1 h-1.5 bg-[#0a0a0a] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                      </div>
                      <span className="text-xs text-white font-mono w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'event' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <select value={selectedEvent} onChange={e => loadEventMetrics(e.target.value)}
              className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none flex-1" data-testid="event-select">
              <option value="">Seleccionar evento</option>
              {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title || e.event_id}</option>)}
            </select>
          </div>
          {eventData ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 className="text-white font-medium text-sm">{eventData.title}</h3>
                <ConfidenceBadge confidence={eventData.confidence} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {[
                  { label: 'Tasa Asistencia', value: `${eventData.attendance.rate}%` },
                  { label: 'Selección Mutua', value: `${eventData.selections.mutual_rate}%` },
                  { label: 'Avance Etapa', value: eventData.stage_advancement },
                  { label: 'Tasa Promoción', value: `${eventData.promotion_rate}%` },
                  { label: 'Tasa Repetición', value: `${eventData.repeat_rate}%` },
                  { label: 'Incidencias', value: eventData.incidents },
                  { label: 'Rating Promedio', value: eventData.feedback.avg_rating },
                  { label: 'Journeys Afectados', value: eventData.journeys_affected },
                  { label: 'Pares Mutuos', value: eventData.selections.mutual_pairs },
                ].map((m, i) => (
                  <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-white">{m.value}</div>
                    <div className="text-[10px] text-gray-500">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-center py-12 text-gray-600 text-sm">Selecciona un evento para ver métricas</div>}
        </div>
      )}

      {view === 'dynamics' && (
        <div className="space-y-4">
          <h3 className="text-white font-medium">Ranking de Dinámicas (Learning Loop)</h3>
          {!dynamicsRanking ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#8B1A1A]" size={20} /></div> :
          dynamicsRanking.length === 0 ? <div className="text-center py-12 text-gray-600 text-sm">Sin datos de rendimiento aún</div> :
          <div className="space-y-1.5">
            {dynamicsRanking.map((d, i) => {
              const recCfg = REC_ACTIONS_DYNAMIC[d.recommendation] || REC_ACTIONS_DYNAMIC.observe;
              return (
                <div key={d.dynamic_id} className="bg-[#111] border border-[#222] rounded-lg p-3 hover:border-[#333] transition-colors" data-testid={`dynamic-rank-${d.dynamic_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 font-mono w-5">#{i+1}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white font-medium">{d.name}</span>
                          <span className="text-[10px] text-gray-600">{d.category}</span>
                          <TrendIcon trend={d.trend} />
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500">
                          <span>Score: <span className="text-white">{d.performance_score}</span></span>
                          <span>Eventos: <span className="text-white">{d.events_used}</span></span>
                          {d.incident_penalty < 0 && <span className="text-red-400">Penalty: {d.incident_penalty}</span>}
                          {d.aggregated && <>
                            <span>Mutual: <span className="text-white">{d.aggregated.avg_mutual_rate}%</span></span>
                            <span>Fricción: <span className={d.aggregated.avg_friction > 20 ? "text-red-400" : "text-white"}>{d.aggregated.avg_friction}%</span></span>
                          </>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: (recCfg.color || '#666') + '15', color: recCfg.color, border: `1px solid ${recCfg.color}30` }}>
                        {recCfg.label}
                      </span>
                      <span className="text-[9px] px-1 py-0.5 rounded bg-[#0a0a0a] text-gray-500">{d.confidence_level}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
        </div>
      )}
    </div>
  );
};


// ============================= PILOT TAB (Pilot Phase) =============================
const OVERRIDE_REASONS = [
  { value: 'staff_observation', label: 'Observacion Staff' },
  { value: 'participant_request', label: 'Solicitud Participante' },
  { value: 'safety_concern', label: 'Seguridad' },
  { value: 'data_correction', label: 'Correccion Datos' },
  { value: 'pilot_adjustment', label: 'Ajuste Piloto' },
  { value: 'incident_response', label: 'Respuesta Incidencia' },
];

const PilotTab = ({ onBack }) => {
  const [view, setView] = useState('config');
  const [config, setConfig] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [overrideStats, setOverrideStats] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackSummary, setFeedbackSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(true);
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const [ovForm, setOvForm] = useState({
    entity_type: 'journey', entity_id: '', action_taken: '',
    override_reason: 'staff_observation', override_impact: 3,
    justification: '', linked_incident_id: ''
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          axios.get(`${API}/pilot/config`, { withCredentials: true }),
          axios.get(`${API}/admin/events`, { withCredentials: true }),
        ]);
        setConfig(cRes.data);
        setEvents(eRes.data?.slice?.(0, 20) || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const loadConversion = async () => {
    try {
      const { data } = await axios.get(`${API}/pilot/metrics/conversion`, { withCredentials: true });
      setConversion(data);
    } catch (e) { console.error(e); }
  };

  const loadOverrides = async () => {
    try {
      const [oRes, sRes] = await Promise.all([
        axios.get(`${API}/pilot/overrides?limit=50`, { withCredentials: true }),
        axios.get(`${API}/pilot/overrides/stats`, { withCredentials: true }),
      ]);
      setOverrides(oRes.data);
      setOverrideStats(sRes.data);
    } catch (e) { console.error(e); }
  };

  const loadFeedback = async (eid) => {
    setSelectedEvent(eid);
    if (!eid) { setFeedbacks([]); setFeedbackSummary(null); return; }
    try {
      const [fRes, sRes] = await Promise.all([
        axios.get(`${API}/pilot/events/${eid}/post-feedback`, { withCredentials: true }),
        axios.get(`${API}/pilot/events/${eid}/post-feedback/summary`, { withCredentials: true }),
      ]);
      setFeedbacks(fRes.data);
      setFeedbackSummary(sRes.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { if (view === 'conversion') loadConversion(); }, [view]);
  useEffect(() => { if (view === 'overrides') loadOverrides(); }, [view]);

  const updateConfig = async (key, value) => {
    try {
      const { data } = await axios.post(`${API}/pilot/config`, { [key]: value }, { withCredentials: true });
      setConfig(data);
    } catch (e) { console.error(e); }
  };

  const submitOverride = async () => {
    try {
      await axios.post(`${API}/pilot/overrides`, ovForm, { withCredentials: true });
      setShowOverrideForm(false);
      loadOverrides();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8B1A1A]" size={28} /></div>;

  return (
    <div className="space-y-4" data-testid="pilot-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <div className="flex items-center gap-1 bg-[#111] border border-[#222] rounded-lg p-0.5">
          {[
            { id: 'config', label: 'Configuracion' },
            { id: 'feedback', label: 'Feedback' },
            { id: 'conversion', label: 'Conversion' },
            { id: 'overrides', label: 'Overrides' }
          ].map(v => (
            <button key={v.id} onClick={() => setView(v.id)}
              className={`px-3 py-1 rounded text-xs transition-colors ${view === v.id ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' : 'text-gray-500'}`}
              data-testid={`pilot-view-${v.id}`}>{v.label}</button>
          ))}
        </div>
      </div>

      {view === 'config' && config && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">Configuracion del Piloto</h3>
          <div className="bg-[#111] border border-[#222] rounded-xl p-4 space-y-3">
            {[
              { key: 'pilot_mode', label: 'Modo Piloto Activo', type: 'bool' },
              { key: 'manual_review_required', label: 'Revision Manual Requerida', type: 'bool' },
              { key: 'post_feedback_enabled', label: 'Feedback Post-Evento Habilitado', type: 'bool' },
              { key: 'override_logging_enabled', label: 'Logging de Overrides', type: 'bool' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0">
                <span className="text-sm text-gray-300">{item.label}</span>
                <button onClick={() => updateConfig(item.key, !config[item.key])}
                  className={`w-10 h-5 rounded-full transition-colors relative ${config[item.key] ? 'bg-[#8B1A1A]' : 'bg-[#333]'}`}
                  data-testid={`toggle-${item.key}`}>
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${config[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-300">Tag de Muestra Piloto</span>
              <span className="text-xs text-[#8B1A1A] bg-[#8B1A1A]/10 px-2 py-1 rounded border border-[#8B1A1A]/30">{config.pilot_sample_tag}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-300">Ventana de Conversion (dias)</span>
              <div className="flex items-center gap-1">
                {[7, 14, 21, 30].map(d => (
                  <button key={d} onClick={() => updateConfig('conversion_window_days', d)}
                    className={`text-xs px-2 py-1 rounded ${config.conversion_window_days === d ? 'bg-[#8B1A1A] text-white' : 'bg-[#0a0a0a] text-gray-500 border border-[#222]'}`}>{d}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {view === 'feedback' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-white font-medium">Feedback Post-Evento</h3>
            <select value={selectedEvent} onChange={e => loadFeedback(e.target.value)}
              className="bg-[#111] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none flex-1 max-w-xs" data-testid="feedback-event-select">
              <option value="">Seleccionar evento</option>
              {events.map(e => <option key={e.event_id} value={e.event_id}>{e.title || e.event_id}</option>)}
            </select>
          </div>
          {feedbackSummary && feedbackSummary.total_responses > 0 && (
            <div className="space-y-2">
              <div className="bg-[#111] border border-[#222] rounded-lg p-4">
                <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Emocional ({feedbackSummary.total_responses} respuestas)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { label: 'Experiencia', value: feedbackSummary.emotional.avg_overall_experience },
                    { label: 'Comodidad', value: feedbackSummary.emotional.avg_comfort_level },
                    { label: 'Energía', value: feedbackSummary.emotional.avg_energy_level },
                    { label: 'Conexion Genuina', value: `${feedbackSummary.emotional.pct_felt_genuine_connection}%` }
                  ].map((m, i) => (
                    <div key={i} className="bg-[#0a0a0a] rounded p-2 text-center">
                      <div className="text-lg font-bold text-white">{m.value}</div>
                      <div className="text-[10px] text-gray-500">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-[#111] border border-[#222] rounded-lg p-4">
                <h4 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Intencion</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { label: 'Repetir Evento', value: `${feedbackSummary.intent.pct_repeat_event}%`, color: '#22c55e' },
                    { label: 'Ver Alguien Otra Vez', value: `${feedbackSummary.intent.pct_see_someone_again}%`, color: '#3b82f6' },
                    { label: 'Experiencia Íntima', value: `${feedbackSummary.intent.pct_intimate_experience}%`, color: '#ec4899' },
                    { label: 'Followup Mutuo', value: feedbackSummary.intent.mutual_followup_pairs, color: '#8B1A1A' }
                  ].map((m, i) => (
                    <div key={i} className="bg-[#0a0a0a] rounded p-2 text-center">
                      <div className="text-lg font-bold" style={{ color: m.color }}>{m.value}</div>
                      <div className="text-[10px] text-gray-500">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          {feedbacks.length > 0 && (
            <div className="space-y-1.5">
              {feedbacks.map(f => (
                <div key={f.feedback_id} className="bg-[#111] border border-[#222] rounded-lg p-3" data-testid={`feedback-${f.feedback_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium">{f.user?.name || '?'}</span>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <span>Exp: {f.overall_experience}/5</span>
                      <span>Comfort: {f.comfort_level}/5</span>
                      <span>Energy: {f.energy_level}/5</span>
                      {f.felt_genuine_connection && <span className="text-green-400">Conexion genuina</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    {f.repeat_event_intent && <span className="text-green-400">Quiere repetir</span>}
                    {f.see_specific_person_again?.length > 0 && <span className="text-blue-400">Quiere ver {f.see_specific_person_again.length} persona(s)</span>}
                    {f.intimate_experience_intent && <span className="text-pink-400">Quiere experiencia intima</span>}
                    <span className="text-gray-600">Confianza: {f.confidence_in_choice}/5</span>
                  </div>
                  {f.comments && <p className="text-xs text-gray-400 mt-1 italic">"{f.comments}"</p>}
                </div>
              ))}
            </div>
          )}
          {selectedEvent && feedbacks.length === 0 && (
            <div className="text-center py-8 text-gray-600 text-sm">Sin feedback para este evento</div>
          )}
        </div>
      )}

      {view === 'conversion' && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">Metricas de Conversion Real</h3>
          {!conversion ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#8B1A1A]" size={20} /></div> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: 'Eventos', value: conversion.overall.total_events },
                  { label: 'Total Asistentes', value: conversion.overall.total_attended },
                  { label: 'Tasa Feedback', value: `${conversion.overall.overall_feedback_rate}%` },
                  { label: 'Tasa Conversion', value: `${conversion.overall.overall_conversion_rate}%`, color: '#22c55e' },
                ].map((m, i) => (
                  <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-3 text-center">
                    <div className="text-xl font-bold" style={{ color: m.color || '#fff' }}>{m.value}</div>
                    <div className="text-[10px] text-gray-500">{m.label}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                {conversion.events.map(e => (
                  <div key={e.event_id} className="bg-[#111] border border-[#222] rounded-lg p-3" data-testid={`conversion-${e.event_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-white font-medium">{e.title || e.event_id}</span>
                      <span className="text-xs text-gray-500">{e.attended} asistentes</span>
                    </div>
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-center">
                      <div><div className="text-sm font-bold text-white">{e.feedback_rate}%</div><div className="text-[9px] text-gray-600">Feedback</div></div>
                      <div><div className="text-sm font-bold text-green-400">{e.real_conversion.conversion_rate}%</div><div className="text-[9px] text-gray-600">Conversion</div></div>
                      <div><div className="text-sm font-bold text-blue-400">{e.desire_to_continue.repeat_event_pct}%</div><div className="text-[9px] text-gray-600">Quiere Repetir</div></div>
                      <div><div className="text-sm font-bold text-purple-400">{e.desire_to_continue.see_someone_again_pct}%</div><div className="text-[9px] text-gray-600">Ver Alguien</div></div>
                      <div><div className="text-sm font-bold text-white">{e.journey_advancements}</div><div className="text-[9px] text-gray-600">Avances</div></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'overrides' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-medium">Overrides Manuales</h3>
            <button onClick={() => setShowOverrideForm(!showOverrideForm)}
              className="flex items-center gap-1 px-3 py-1.5 bg-[#8B1A1A] text-white rounded text-xs" data-testid="new-override-btn">
              <Plus size={14} /> Nuevo Override
            </button>
          </div>
          {overrideStats && (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
                <div className="text-lg font-bold text-white">{overrideStats.total}</div>
                <div className="text-[10px] text-gray-500">Total</div>
              </div>
              {Object.entries(overrideStats.by_reason || {}).slice(0, 2).map(([r, c]) => (
                <div key={r} className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
                  <div className="text-lg font-bold text-[#8B1A1A]">{c}</div>
                  <div className="text-[10px] text-gray-500">{r.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          )}
          {showOverrideForm && (
            <div className="bg-[#111] border border-[#8B1A1A]/30 rounded-xl p-4 space-y-2" data-testid="override-form">
              <div className="grid grid-cols-2 gap-2">
                <select value={ovForm.entity_type} onChange={e => setOvForm(f => ({ ...f, entity_type: e.target.value }))}
                  className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="ov-entity-type">
                  {['journey', 'dynamic', 'user', 'event'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={ovForm.entity_id} onChange={e => setOvForm(f => ({ ...f, entity_id: e.target.value }))}
                  placeholder="Entity ID" className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="ov-entity-id" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select value={ovForm.override_reason} onChange={e => setOvForm(f => ({ ...f, override_reason: e.target.value }))}
                  className="bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" data-testid="ov-reason">
                  {OVERRIDE_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-gray-500">Impacto:</span>
                  {[1,2,3,4,5].map(w => (
                    <button key={w} onClick={() => setOvForm(f => ({ ...f, override_impact: w }))}
                      className={`w-6 h-6 rounded text-xs ${ovForm.override_impact === w ? 'bg-[#8B1A1A] text-white' : 'bg-[#0a0a0a] text-gray-500 border border-[#222]'}`}>{w}</button>
                  ))}
                </div>
              </div>
              <input value={ovForm.action_taken} onChange={e => setOvForm(f => ({ ...f, action_taken: e.target.value }))}
                placeholder="Accion tomada..." className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none" />
              <textarea value={ovForm.justification} onChange={e => setOvForm(f => ({ ...f, justification: e.target.value }))}
                placeholder="Justificacion..." rows={2} className="w-full bg-[#0a0a0a] border border-[#222] rounded px-2 py-1.5 text-xs text-white outline-none resize-none" data-testid="ov-justification" />
              <div className="flex gap-2">
                <button onClick={submitOverride} className="px-3 py-1.5 bg-[#8B1A1A] text-white rounded text-xs" data-testid="submit-override">Registrar</button>
                <button onClick={() => setShowOverrideForm(false)} className="px-3 py-1.5 bg-[#222] text-gray-400 rounded text-xs">Cancelar</button>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            {overrides.map(o => (
              <div key={o.override_id} className="bg-[#111] border border-[#222] rounded-lg p-3" data-testid={`override-${o.override_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#8B1A1A]/15 text-[#8B1A1A] border border-[#8B1A1A]/30">{o.entity_type}</span>
                    <span className="text-sm text-white">{o.action_taken || 'Override'}</span>
                    <span className="text-[10px] text-gray-600">{o.override_reason.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-gray-500">
                    <span>Impacto: {o.override_impact}/5</span>
                    <span>{o.admin_name}</span>
                  </div>
                </div>
                {o.justification && <p className="text-xs text-gray-400">{o.justification}</p>}
                {(o.before_state && Object.keys(o.before_state).length > 0) && (
                  <div className="text-[10px] text-gray-600 mt-1">
                    Antes: {JSON.stringify(o.before_state)} → Despues: {JSON.stringify(o.after_state)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};



// ============================= SYSTEM ACTIONS TAB (Sprint 2.5) =============================
const ACTION_SECTIONS = [
  { key: 'facilitate', label: 'Facilitar Conexion', icon: Heart, color: '#8B1A1A', desc: 'Parejas listas para contacto directo' },
  { key: 'prioritize', label: 'Priorizar', icon: Star, color: '#f59e0b', desc: 'Seguimiento activo requerido' },
  { key: 'invite_event', label: 'Invitar a Evento', icon: Calendar, color: '#3b82f6', desc: 'Necesitan otro evento para confirmar patron' },
  { key: 'observe', label: 'Observar', icon: Eye, color: '#6b7280', desc: 'Sin accion inmediata' },
  { key: 'discard', label: 'Descartar', icon: X, color: '#374151', desc: 'Friccion o estancamiento' }
];

const USER_ACTION_SECTIONS = [
  { key: 'invite_to_event', label: 'Invitar a Evento', icon: Calendar, color: '#3b82f6', desc: 'Alto potencial o alta demanda' },
  { key: 'move_to_premium', label: 'Mover a Premium', icon: Star, color: '#f59e0b', desc: 'Candidatos premium identificados' },
  { key: 'intervene', label: 'Intervenir', icon: AlertTriangle, color: '#ef4444', desc: 'Journeys estancados, requiere atencion' },
  { key: 'observe_user', label: 'Observar', icon: Eye, color: '#6b7280', desc: 'Bajo engagement detectado' }
];

const TAG_COLORS = {
  high_potential: '#22c55e', low_engagement: '#6b7280', selective_user: '#8b5cf6',
  high_demand_user: '#3b82f6', premium_candidate: '#f59e0b', requires_intervention: '#ef4444'
};

const ExplainPanel = ({ rec }) => {
  if (!rec?.explainability) return null;
  const v = rec.explainability.variables;
  const st = rec.explainability.stagnation;
  return (
    <div className="mt-2 bg-[#0a0a0a] border border-[#1a1a1a] rounded p-2 text-[10px] text-gray-500 space-y-1" data-testid="explain-panel">
      <div className="text-gray-400 font-medium mb-1">Variables de decision:</div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-0.5">
        <span>Afinidad: <span className="text-white">{v.affinity_score}</span></span>
        <span>Intensidad: <span className="text-white">{v.intensity_score}</span></span>
        <span>Consistencia: <span className="text-white">{v.consistency_score}</span></span>
        <span>Mutuas: <span className="text-white">{v.mutual_selections}</span></span>
        <span>Eventos: <span className="text-white">{v.shared_events}</span></span>
        <span>Delta: <span className="text-white">{v.last_event_delta}</span></span>
        <span>Positivas: <span className="text-white">{v.positive_interactions}</span></span>
        <span>Negativas: <span className="text-white">{v.negative_interactions}</span></span>
        <span>Estancado: <span className={v.is_stagnated ? "text-red-400" : "text-green-400"}>{v.is_stagnated ? 'Si' : 'No'}</span></span>
      </div>
      {st?.is_stagnated && st.reasons?.length > 0 && (
        <div className="text-red-400/80 mt-1">Estancamiento: {st.reasons.join(', ')}</div>
      )}
    </div>
  );
};

const SystemActionsTab = ({ onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewLevel, setViewLevel] = useState('journey');
  const [expandedExplain, setExpandedExplain] = useState({});

  const fetchActions = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/journeys/system-actions`, { withCredentials: true });
      setData(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchActions(); }, [fetchActions]);

  const toggleExplain = (id) => setExpandedExplain(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#8B1A1A]" size={28} /></div>;
  if (!data) return <div className="text-gray-500 text-center py-12">Error cargando acciones</div>;

  const { journey_actions, user_actions, summary } = data;

  return (
    <div className="space-y-4" data-testid="system-actions-tab">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm"><ArrowLeft size={16} /> Volver</button>
        <div className="flex items-center gap-1 bg-[#111] border border-[#222] rounded-lg p-0.5">
          <button onClick={() => setViewLevel('journey')}
            className={`px-3 py-1 rounded text-xs transition-colors ${viewLevel === 'journey' ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' : 'text-gray-500'}`}
            data-testid="level-journey">Por Journey</button>
          <button onClick={() => setViewLevel('user')}
            className={`px-3 py-1 rounded text-xs transition-colors ${viewLevel === 'user' ? 'bg-[#8B1A1A]/20 text-[#8B1A1A]' : 'text-gray-500'}`}
            data-testid="level-user">Por Usuario</button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        {[
          { label: 'Facilitar', value: summary.facilitate_count, color: '#8B1A1A' },
          { label: 'Priorizar', value: summary.prioritize_count, color: '#f59e0b' },
          { label: 'Invitar', value: summary.invite_count, color: '#3b82f6' },
          { label: 'Observar', value: summary.observe_count, color: '#6b7280' },
          { label: 'Descartar', value: summary.discard_count, color: '#374151' }
        ].map((c, i) => (
          <div key={i} className="bg-[#111] border border-[#222] rounded-lg p-2 text-center">
            <div className="text-lg font-bold" style={{ color: c.color }}>{c.value}</div>
            <div className="text-[10px] text-gray-500">{c.label}</div>
          </div>
        ))}
      </div>

      {viewLevel === 'journey' ? (
        <div className="space-y-4">
          {ACTION_SECTIONS.map(section => {
            const items = journey_actions[section.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={section.key} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl overflow-hidden" data-testid={`action-section-${section.key}`}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <section.icon size={14} style={{ color: section.color }} />
                    <span className="text-sm font-medium text-white">{section.label}</span>
                    <span className="text-xs text-gray-500 font-mono bg-[#111] px-1.5 rounded">{items.length}</span>
                  </div>
                  <span className="text-[10px] text-gray-600">{section.desc}</span>
                </div>
                <div className="divide-y divide-[#111]">
                  {items.map(item => {
                    const rec = item.recommendation;
                    const isExpanded = expandedExplain[item.journey_id];
                    return (
                      <div key={item.journey_id} className="px-4 py-2.5 hover:bg-[#111]/50 transition-colors" data-testid={`action-item-${item.journey_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm text-white font-medium">{item.user_a?.name || '?'}</span>
                              <span className="text-[10px] text-gray-600">&amp;</span>
                              <span className="text-sm text-white font-medium">{item.user_b?.name || '?'}</span>
                            </div>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#111] text-gray-400 border border-[#222]">{STAGE_CONFIG[item.current_stage]?.label || item.current_stage}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                                <span>aff:<span className="text-white ml-0.5">{item.affinity_score}</span></span>
                                <span>int:<span className="text-white ml-0.5">{item.intensity}</span></span>
                                <span>con:<span className="text-white ml-0.5">{item.consistency}</span></span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {rec.urgency && rec.urgency !== 'none' && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: URGENCY_COLORS[rec.urgency] + '20', color: URGENCY_COLORS[rec.urgency] }}>
                                  {rec.urgency === 'high' ? 'Urgente' : rec.urgency === 'medium' ? 'Media' : 'Baja'}
                                </span>
                              )}
                              <button onClick={() => toggleExplain(item.journey_id)}
                                className="text-gray-600 hover:text-gray-400 p-1" title="Ver variables"
                                data-testid={`explain-toggle-${item.journey_id}`}>
                                {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="text-[11px] text-gray-500 mt-1">{rec.detail}</div>
                        {isExpanded && <ExplainPanel rec={rec} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {Object.values(journey_actions).every(a => a.length === 0) && (
            <div className="text-center py-12 text-gray-500 text-sm">No hay acciones pendientes a nivel journey.</div>
          )}
        </div>
      ) : (
        /* USER LEVEL VIEW */
        <div className="space-y-4">
          {USER_ACTION_SECTIONS.map(section => {
            const items = user_actions[section.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={section.key} className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl overflow-hidden" data-testid={`user-action-section-${section.key}`}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a]">
                  <div className="flex items-center gap-2">
                    <section.icon size={14} style={{ color: section.color }} />
                    <span className="text-sm font-medium text-white">{section.label}</span>
                    <span className="text-xs text-gray-500 font-mono bg-[#111] px-1.5 rounded">{items.length}</span>
                  </div>
                  <span className="text-[10px] text-gray-600">{section.desc}</span>
                </div>
                <div className="divide-y divide-[#111]">
                  {items.map((item, idx) => (
                    <div key={item.user?.user_id || idx} className="px-4 py-2.5 hover:bg-[#111]/50" data-testid={`user-action-item-${item.user?.user_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white font-medium">{item.user?.name || '?'}</span>
                          <div className="flex gap-1">
                            {item.tags?.map((t, ti) => (
                              <span key={ti} className="text-[9px] px-1.5 py-0.5 rounded border" style={{ borderColor: (TAG_COLORS[t.tag] || '#666') + '40', color: TAG_COLORS[t.tag] || '#666', backgroundColor: (TAG_COLORS[t.tag] || '#666') + '10' }}>
                                {t.label}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-gray-500">
                          <span>Journeys: <span className="text-white">{item.stats?.total_journeys}</span></span>
                          <span>Afinidad: <span className="text-white">{item.stats?.avg_affinity}</span></span>
                          <span>Mutuas: <span className="text-white">{item.stats?.total_mutual_selections}</span></span>
                          <span>Seleccionado: <span className="text-white">{item.stats?.selected_by_count}x</span></span>
                        </div>
                      </div>
                      {item.tags?.map((t, ti) => (
                        <div key={ti} className="text-[10px] text-gray-600 mt-0.5 ml-4">{t.reason}</div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {Object.values(user_actions).every(a => a.length === 0) && (
            <div className="text-center py-12 text-gray-500 text-sm">No hay acciones pendientes a nivel usuario.</div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================= MAIN COMPONENT =============================
const EventLab = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      axios.get(`${API}/lab/overview`, { withCredentials: true })
        .then(r => setOverview(r.data))
        .catch(console.error);
    }
  }, [user]);

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-gray-500">Acceso restringido</div>;
  }

  const TABS = [
    { id: 'overview', label: 'Resumen', icon: BarChart3 },
    { id: 'dynamics', label: 'Dinámicas', icon: Sparkles },
    { id: 'flows', label: 'Flujos', icon: ListOrdered },
    { id: 'events', label: 'Eventos', icon: Calendar },
    { id: 'journeys', label: 'Journeys', icon: GitBranch },
    { id: 'actions', label: 'Acciones', icon: Zap },
    { id: 'incidents', label: 'Incidencias', icon: FileWarning },
    { id: 'metrics', label: 'Métricas', icon: Activity },
    { id: 'pilot', label: 'Piloto', icon: Shield, accent: true }
  ];

  return (
    <div className="min-h-screen bg-black" data-testid="event-lab-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical size={20} className="text-[#8B1A1A]" />
            <h1 className="text-xl font-bold text-white">Laboratorio de Eventos</h1>
          </div>
          <p className="text-xs text-gray-500">Control operativo, curaduría de dinámicas y evolución del piloto</p>
        </div>

        <div className="flex gap-1 mb-6 border-b border-[#222] pb-2 flex-wrap">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? tab.accent
                    ? 'bg-[#8B1A1A]/30 text-[#ff6b6b] border border-[#8B1A1A]/50 shadow-[0_0_8px_rgba(139,26,26,0.3)]'
                    : 'bg-[#8B1A1A]/20 text-[#8B1A1A] border border-[#8B1A1A]/30'
                  : tab.accent
                    ? 'text-[#8B1A1A] hover:text-[#ff6b6b] border border-[#8B1A1A]/20 bg-[#8B1A1A]/5'
                    : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && <OverviewTab overview={overview} onNavigate={setActiveTab} />}
        {activeTab === 'dynamics' && <DynamicsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'flows' && <FlowsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'events' && <EventsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'journeys' && <JourneysTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'actions' && <SystemActionsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'incidents' && <IncidentsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'metrics' && <MetricsTab onBack={() => setActiveTab('overview')} />}
        {activeTab === 'pilot' && <PilotTab onBack={() => setActiveTab('overview')} />}
      </div>
    </div>
  );
};

export default EventLab;
